import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Globe, Linkedin, Facebook, MessageCircle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { PROFILE } from "../data/projects";
import { SectionHeading } from "./Portfolio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-sm border border-[#212529]/15 bg-white px-4 py-3 text-sm font-normal text-[#212529] outline-none transition-colors duration-300 placeholder:text-[#6c757d]/70 focus:border-accent";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("send failed");
      toast.success("Message sent — thank you. I'll be in touch soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send your message right now. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-24">
      <div className="mx-auto max-w-6xl px-6">
        <SectionHeading eyebrow="Let's Talk" title="Get In Touch" />
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-5">
          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-5 lg:col-span-3"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <input
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Name"
                className={inputCls}
              />
              <input
                data-testid="contact-email-input"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                className={inputCls}
              />
            </div>
            <textarea
              data-testid="contact-message-input"
              required
              rows={7}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Write your message..."
              className={`${inputCls} resize-none`}
            />
            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={sending}
              className="flex items-center gap-2 rounded-full bg-accent px-8 py-3 text-sm font-medium uppercase tracking-wider text-white transition-all duration-400 hover:-translate-y-0.5 hover:brightness-110 disabled:opacity-60"
            >
              {sending ? (
                <>
                  Sending <Loader2 className="h-4 w-4 animate-spin" />
                </>
              ) : (
                <>
                  Send Message <Send className="h-4 w-4" />
                </>
              )}
            </button>
          </motion.form>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-8 lg:col-span-2"
          >
            <div data-testid="contact-info-location" className="flex gap-4">
              <MapPin className="mt-1 h-5 w-5 shrink-0 text-accent" />
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-widest text-[#212529]">Location</h4>
                <p className="mt-1 text-sm text-[#6c757d]">{PROFILE.location}</p>
              </div>
            </div>
            <div data-testid="contact-info-behance" className="flex gap-4">
              <Globe className="mt-1 h-5 w-5 shrink-0 text-accent" />
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-widest text-[#212529]">Behance</h4>
                <a
                  data-testid="contact-behance-link"
                  href={PROFILE.behance}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 block text-sm text-[#6c757d] transition-colors hover:text-accent"
                >
                  behance.net/travelpartner
                </a>
              </div>
            </div>
            <div data-testid="contact-info-email" className="flex gap-4">
              <Mail className="mt-1 h-5 w-5 shrink-0 text-accent" />
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-widest text-[#212529]">Email</h4>
                <a
                  data-testid="contact-email-link"
                  href={`mailto:${PROFILE.email}`}
                  className="mt-1 block text-sm text-[#6c757d] transition-colors hover:text-accent"
                >
                  {PROFILE.email}
                </a>
              </div>
            </div>
            <div data-testid="contact-info-whatsapp" className="flex gap-4">
              <MessageCircle className="mt-1 h-5 w-5 shrink-0 text-accent" />
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-widest text-[#212529]">WhatsApp</h4>
                <a
                  data-testid="contact-whatsapp-link"
                  href={PROFILE.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 block text-sm text-[#6c757d] transition-colors hover:text-accent"
                >
                  {PROFILE.whatsappDisplay}
                </a>
              </div>
            </div>
            <div data-testid="contact-info-linkedin" className="flex gap-4">
              <Linkedin className="mt-1 h-5 w-5 shrink-0 text-accent" />
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-widest text-[#212529]">LinkedIn</h4>
                <a
                  data-testid="contact-linkedin-link"
                  href={PROFILE.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 block text-sm text-[#6c757d] transition-colors hover:text-accent"
                >
                  Connect with me
                </a>
              </div>
            </div>
            <div data-testid="contact-info-facebook" className="flex gap-4">
              <Facebook className="mt-1 h-5 w-5 shrink-0 text-accent" />
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-widest text-[#212529]">Facebook</h4>
                <a
                  data-testid="contact-facebook-link"
                  href={PROFILE.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 block text-sm text-[#6c757d] transition-colors hover:text-accent"
                >
                  facebook.com/harshithholla
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
